/**
 * Dragging a bar to change its dates (T7.31, design.md § 7.7).
 *
 * A bar is a RENDERING of one or more Schedule rows merged into one span, so a
 * drag is several writes, not one. The API has no notion of a bar and takes an
 * absolute start and end per row — deliberately, because a route that took a
 * delta would have to decide what "the bar" is.
 *
 *   body drag   every underlying row shifts by the same whole-day delta
 *   left edge   only the EARLIEST start moves; every other row is untouched
 *   right edge  only the LATEST end moves
 *
 * **Nothing is recalculated.** A date change moves no percentage, no weighting
 * and no other room. Werner judges the impact of a date change himself, as with
 * leave; there is no re-planning here and no overlap warning, by design.
 *
 * The write follows click-to-cycle's shape (`useDraftEditor`): optimistic first
 * so the bar stays where it was dropped, then one PATCH per row through a single
 * promise chain so they arrive in order, then reconcile against the schedule the
 * API sends back — the API is authoritative. On any failure every row in that
 * drag goes back to where it started and the failure is shown. A bar is never
 * left sitting at dates the database refused.
 *
 * Nothing here reads the DOM to update a bar. The drag reads the track's width
 * to convert pixels to days, which is a measurement, not a patch: every bar on
 * screen is still rendered from state.
 */
import { useCallback, useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { blueprintKeys, type VersionPayload } from '../../lib/blueprints';
import { PARTY_LABEL, type TimelineBar, type TimelineRow } from './layout';

const MS = 86400000;

export type DragMode = 'move' | 'start' | 'end';

/** What the caller must know for a write: which version, and which cache entry holds it. */
export interface TimelineEditing {
  versionId: string;
  queryKey: QueryKey;
}

/** The drag in progress, so the view can draw the bar where the cursor has it. */
export interface DragPreview {
  key: string;
  mode: DragMode;
  deltaDays: number;
}

export interface DragFailure {
  /** Room and party in full — nothing about a bar is truncated. */
  what: string;
  message: string;
  /**
   * The version this drag named was published by somebody else while it was
   * open, and the API refused the write rather than let it land in a version
   * nobody can change (409 `version_published`). Not a generic failure: the
   * draft on screen is gone, so the notice says so and the whole blueprint is
   * re-read. The same case `useDraftEditor` handles for click-to-cycle.
   */
  superseded: boolean;
}

export interface ScheduleDrag {
  preview: DragPreview | null;
  /** Writes sent and not yet answered. */
  pending: number;
  failure: DragFailure | null;
  dismissFailure: () => void;
  /**
   * True when the click that is about to fire is the tail of a drag and should
   * not open the deliverable list. Reading it clears it.
   */
  consumeClick: () => boolean;
  /** Attach to a bar's pointerdown. Does nothing when the bar has no row ids. */
  begin: (
    event: React.PointerEvent<HTMLElement>,
    row: TimelineRow,
    bar: TimelineBar,
    mode: DragMode,
  ) => void;
}

export function barKey(row: TimelineRow, bar: TimelineBar): string {
  return `${row.roomID}|${bar.party}`;
}

/** ISO date `n` days on. Done in UTC, as every other date in this feature is. */
export function addDays(iso: string, n: number): string {
  return new Date(Date.parse(iso + 'T00:00:00Z') + n * MS).toISOString().slice(0, 10);
}

const dayMs = (iso: string): number => Date.parse(iso + 'T00:00:00Z');

/** The new dates for each row of a bar, or `null` where the drag changes nothing. */
export function datesAfterDrag(
  bar: TimelineBar,
  mode: DragMode,
  deltaDays: number,
): { id: string; startDate: string; endDate: string }[] {
  if (!deltaDays || !bar.addressable) return [];
  const out: { id: string; startDate: string; endDate: string }[] = [];

  if (mode === 'move') {
    for (const r of bar.rows) {
      if (!r.id) continue;
      out.push({ id: r.id, startDate: addDays(r.start, deltaDays), endDate: addDays(r.end, deltaDays) });
    }
    return out;
  }

  if (mode === 'start') {
    // The earliest start IS the bar's left edge. Ties are impossible to tell
    // apart visually, so the first row at that date is the one that moves.
    const target = bar.rows.find((r) => r.start === bar.start);
    if (!target?.id) return [];
    // A row may not be dragged inside out; its own end is the floor.
    const startDate = addDays(target.start, deltaDays);
    if (startDate > target.end) return [];
    return [{ id: target.id, startDate, endDate: target.end }];
  }

  const target = [...bar.rows].reverse().find((r) => r.end === bar.end);
  if (!target?.id) return [];
  const endDate = addDays(target.end, deltaDays);
  if (endDate < target.start) return [];
  return [{ id: target.id, startDate: target.start, endDate }];
}

/**
 * How far a drag may go before a row turns inside out, in days.
 *
 * Clamping while dragging rather than only on drop means the bar never shows a
 * span the API would refuse, so nothing has to snap back for a reason the person
 * could not see coming.
 */
function clampDelta(bar: TimelineBar, mode: DragMode, deltaDays: number): number {
  if (mode === 'move') return deltaDays;
  if (mode === 'start') {
    const target = bar.rows.find((r) => r.start === bar.start);
    if (!target) return 0;
    const limit = Math.round((dayMs(target.end) - dayMs(target.start)) / MS);
    return Math.min(deltaDays, limit);
  }
  const target = [...bar.rows].reverse().find((r) => r.end === bar.end);
  if (!target) return 0;
  const limit = -Math.round((dayMs(target.end) - dayMs(target.start)) / MS);
  return Math.max(deltaDays, limit);
}

export function useScheduleDrag({
  editing,
  rangeDayCount,
}: {
  /** Absent for a viewer or a published version: `begin` then does nothing. */
  editing: TimelineEditing | undefined;
  rangeDayCount: number;
}): ScheduleDrag {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const [pending, setPending] = useState(0);
  const [failure, setFailure] = useState<DragFailure | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  // A drag ends in a click on the same element. Without this, every drag would
  // also open the deliverable list on top of what was just moved.
  const swallow = useRef(false);

  const commit = useCallback(
    (what: string, changes: { id: string; startDate: string; endDate: string }[]) => {
      if (!editing || !changes.length) return;
      const { versionId, queryKey } = editing;

      const before = queryClient.getQueryData<VersionPayload>(queryKey);
      if (!before) return;

      // Optimistic: the rows move in a COPY of the schedule, so the cached
      // payload is never mutated in place — React Query compares by reference.
      const byId = new Map(changes.map((c) => [c.id, c]));
      queryClient.setQueryData<VersionPayload>(queryKey, {
        ...before,
        schedule: before.schedule.map((r) => {
          const hit = (r as { id?: string }).id ? byId.get((r as { id?: string }).id as string) : undefined;
          return hit ? { ...r, StartDate: hit.startDate, EndDate: hit.endDate } : r;
        }),
      });

      setFailure(null);
      setPending((n) => n + changes.length);

      chain.current = chain.current.then(async () => {
        try {
          let last: { schedule?: VersionPayload['schedule'] } | undefined;
          for (const c of changes) {
            last = await api.patch<{ schedule?: VersionPayload['schedule'] }>(
              `/versions/${versionId}/schedule/${c.id}`,
              { startDate: c.startDate, endDate: c.endDate },
            );
            setPending((n) => n - 1);
          }
          // Reconcile: the API sends the version's schedule as it now stands,
          // and the API is authoritative. If it disagrees with what is drawn,
          // what is drawn is wrong.
          const authoritative = last?.schedule;
          const current = queryClient.getQueryData<VersionPayload>(queryKey);
          if (authoritative && Array.isArray(authoritative) && current) {
            queryClient.setQueryData<VersionPayload>(queryKey, { ...current, schedule: authoritative });
          }
        } catch (e) {
          setPending(0);
          const superseded = e instanceof ApiError && e.isVersionPublished;
          setFailure({
            what,
            superseded,
            message: superseded
              ? 'Somebody published this version while you were dragging, so the dates were not changed.'
              : e instanceof Error
                ? e.message
                : 'The dates were not saved.',
          });
          // Roll back VISIBLY: the bar goes back where it was, rather than
          // standing at dates the database refused. The refetch then settles
          // anything this drag could not know about.
          const current = queryClient.getQueryData<VersionPayload>(queryKey);
          if (current) queryClient.setQueryData<VersionPayload>(queryKey, { ...current, schedule: before.schedule });
          void queryClient.invalidateQueries({ queryKey });
          // A publish means a NEW draft exists and the version list is stale, so
          // re-read the whole blueprint rather than only the payload this screen
          // happens to hold.
          if (superseded) void queryClient.invalidateQueries({ queryKey: blueprintKeys.all });
        }
      });
    },
    [editing, queryClient],
  );

  const begin = useCallback(
    (event: React.PointerEvent<HTMLElement>, row: TimelineRow, bar: TimelineBar, mode: DragMode) => {
      if (!editing || !bar.addressable || event.button !== 0) return;
      const track = event.currentTarget.closest('.tl-track') as HTMLElement | null;
      if (!track) return;

      const trackWidth = track.getBoundingClientRect().width;
      if (!trackWidth || !rangeDayCount) return;

      event.preventDefault();
      event.stopPropagation();

      const key = barKey(row, bar);
      const startX = event.clientX;
      const perDay = trackWidth / rangeDayCount;
      let delta = 0;
      let moved = false;

      const onMove = (e: PointerEvent) => {
        const raw = Math.round((e.clientX - startX) / perDay);
        const next = clampDelta(bar, mode, raw);
        if (Math.abs(e.clientX - startX) > 2) moved = true;
        if (next !== delta) {
          delta = next;
          setPreview({ key, mode, deltaDays: next });
        }
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        setPreview(null);
        if (moved) {
          swallow.current = true;
          // A drag that ends somewhere other than on the bar produces no click
          // at all, so the flag must not survive to swallow an unrelated one.
          window.setTimeout(() => {
            swallow.current = false;
          }, 0);
        }
        if (delta) {
          commit(`${row.name} — ${PARTY_LABEL[bar.party]}`, datesAfterDrag(bar, mode, delta));
        } else if (moved) {
          // Dragged, but not far enough to be a whole day. Nothing to write.
        }
      };

      const onCancel = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        setPreview(null);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      setPreview({ key, mode, deltaDays: 0 });
    },
    [editing, rangeDayCount, commit],
  );

  return {
    preview,
    pending,
    failure,
    dismissFailure: () => setFailure(null),
    consumeClick: () => {
      const was = swallow.current;
      swallow.current = false;
      return was;
    },
    begin,
  };
}
